import React, { useState } from 'react';
import { Check, CheckCircle2, ListChecks } from 'lucide-react';
import type { RoutyChoiceOption, RoutyChoicesArtifact } from '../../business/routy/routyArtifacts';

interface ChoicesCardProps {
  artifact: RoutyChoicesArtifact;
  disabled?: boolean;
  onSubmit: (artifact: RoutyChoicesArtifact, picked: RoutyChoiceOption[]) => void;
}

/**
 * Tappable answers to a question Routy asked (`present_choices`). Single-select
 * answers on tap; multi-select collects picks and answers on Done.
 */
const ChoicesCard: React.FC<ChoicesCardProps> = ({ artifact, disabled, onSubmit }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const locked = disabled || submitted;

  if (artifact.options.length === 0) return null;

  const submit = (ids: string[]) => {
    const picked = artifact.options.filter((option) => ids.includes(option.id));
    if (picked.length === 0) return;
    setSubmitted(true);
    onSubmit(artifact, picked);
  };

  const handleTap = (id: string) => {
    if (!artifact.allowMultiple) {
      setSelected([id]);
      submit([id]);
      return;
    }
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-sm">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <ListChecks className="w-4 h-4 text-accent-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-ink-900">{artifact.prompt}</span>
      </div>

      <div className="p-3 space-y-2">
        {artifact.allowMultiple && <p className="text-2xs text-ink-400">Pick all that apply.</p>}
        {artifact.options.map((option) => {
          const isSelected = selected.includes(option.id);
          return (
            // eslint-disable-next-line no-restricted-syntax -- Full-width selectable option row with a checkbox; no Button variant is a toggle row
            <button
              key={option.id}
              type="button"
              onClick={() => handleTap(option.id)}
              disabled={locked}
              aria-pressed={artifact.allowMultiple ? isSelected : undefined}
              className={`w-full flex items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed ${
                isSelected ? 'border-accent-300 bg-accent-50' : 'border-ink-200 bg-surface hover:bg-ink-50'
              } ${locked && !isSelected ? 'opacity-50' : ''}`}
            >
              {artifact.allowMultiple && (
                <span
                  className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                    isSelected ? 'border-accent-600 bg-accent-600 text-white' : 'border-ink-300 bg-surface'
                  }`}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </span>
              )}
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-ink-900">{option.label}</span>
                {option.description && (
                  <span className="block text-2xs text-ink-500 leading-snug">{option.description}</span>
                )}
              </span>
            </button>
          );
        })}

        {artifact.allowMultiple && (
          // eslint-disable-next-line no-restricted-syntax -- Accent full-width CTA matching the other Routy cards; Button's primary is ink-900
          <button
            type="button"
            onClick={() => submit(selected)}
            disabled={locked || selected.length === 0}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-accent-600 text-white text-xs font-semibold hover:bg-accent-700 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {selected.length > 0 ? `Done (${selected.length})` : 'Done'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ChoicesCard;
